/**
 * Letter Zoom Animation Utilities
 *
 * Utility functions specific to the Letter Zoom animation variant.
 * Handles zoom step calculations, text extraction, and visual styling.
 */

import {
  LETTER_ZOOM_STEPS,
  Y2K_GRADIENT_COLORS,
  type ZoomStep,
} from '../types';

/**
 * Pre-calculated step boundaries for weighted timing
 * Array of [startPercent, endPercent] tuples for O(n) lookup
 */
const STEP_BOUNDARIES = calculateStepBoundaries();

/**
 * Calculate cumulative weights for weighted step timing
 * @returns Array of [startPercent, endPercent] for each step
 */
function calculateStepBoundaries(): Array<[number, number]> {
  const boundaries: Array<[number, number]> = [];
  let cumulative = 0;
  for (const step of LETTER_ZOOM_STEPS) {
    const start = cumulative;
    cumulative += step.weight;
    boundaries.push([start, cumulative]);
  }
  return boundaries;
}

/**
 * Get text content for current zoom step
 * Extended semantic extraction: letter → word → ... → 3 lines
 *
 * @param words - Array of words from text
 * @param step - Current zoom step config
 * @param linesPerScreen - How many words fit in ~3 lines (default: 35)
 * @returns Extracted text for current zoom level
 */
export function getZoomStepText(
  words: string[],
  step: ZoomStep,
  linesPerScreen: number = 35
): string {
  if (step.getTextLength === 'word') {
    return words[0] || '';
  }
  if (step.getTextLength === 'lines3') {
    // ~35 words fills 3 lines at typical viewport
    return words.slice(0, Math.min(linesPerScreen, words.length)).join(' ');
  }
  if (typeof step.getTextLength === 'number') {
    if (step.getTextLength === 1) {
      return words[0]?.charAt(0) || '';
    }
    return words.slice(0, Math.min(step.getTextLength, words.length)).join(' ');
  }
  return words[0] || '';
}

/**
 * Get Y2K glow color based on scale
 * Pink (8-3.5x) → Lavender (2.5-1.15x) → Mint/Teal (1.05-1.0x)
 *
 * @param scale - Current zoom scale
 * @returns Y2K gradient color for glow effect
 */
export function getZoomGlowColor(scale: number): string {
  if (scale >= 3.5) return Y2K_GRADIENT_COLORS.pink;
  if (scale >= 1.1) return Y2K_GRADIENT_COLORS.lavender;
  return Y2K_GRADIENT_COLORS.mint;
}

/**
 * Find current step index based on weighted emergence progress
 * Uses pre-calculated boundaries for O(n) lookup (9 steps max)
 *
 * @param emergenceProgress - Progress through emergence phase (0-1)
 * @returns Step index (0 to LETTER_ZOOM_STEPS.length - 1)
 */
export function findStepIndex(emergenceProgress: number): number {
  for (let i = 0; i < STEP_BOUNDARIES.length; i++) {
    const [, end] = STEP_BOUNDARIES[i];
    if (emergenceProgress <= end) return i;
  }
  return STEP_BOUNDARIES.length - 1;
}

/**
 * Get interpolation fraction within current step
 *
 * @param emergenceProgress - Progress through emergence phase (0-1)
 * @param stepIndex - Current step index
 * @returns 0-1 value representing progress through current step
 */
export function getStepFraction(emergenceProgress: number, stepIndex: number): number {
  const [start, end] = STEP_BOUNDARIES[stepIndex];
  const stepDuration = end - start;
  if (stepDuration <= 0) return 1;
  return Math.min((emergenceProgress - start) / stepDuration, 1);
}

/**
 * Export step boundaries for testing/debugging
 */
export function getStepBoundaries(): ReadonlyArray<[number, number]> {
  return STEP_BOUNDARIES;
}
